import React from 'react';

import { Box } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { useAuth } from '@opendoor/auth-fe/context';
import { IconTags } from '@tabler/icons-react';

import { isDashboardSlug } from '../../utils/slug';
import { GlassNavLink } from './glass/GlassNavLink';

const DASHBOARD_HREF = '/dashboard';
const DASHBOARD_LABEL = 'Dashboard';
const DASHBOARD_ARIA_LABEL = 'View Dashboard';
const DASHBOARD_SKELETON_WIDTH_CLASS = 'w-[84px]';
const DASHBOARD_ICON_SIZE = 20;
const DASHBOARD_ICON_STROKE = 1.5;

type DashboardNavVariant = 'standard' | 'glass';

interface DashboardNavItemProps {
  slug: string;
  variant: DashboardNavVariant;
}

export const DashboardNavItem: React.FC<DashboardNavItemProps> = ({ slug, variant }) => {
  const { authentication } = useAuth();

  if (authentication.state === 'authenticating') {
    return <DashboardNavSkeleton variant={variant} />;
  }

  if (authentication.state !== 'authenticated') {
    return null;
  }

  if (variant === 'glass') {
    return (
      <Box role="listitem" className="relative mr-8 block">
        <GlassNavLink
          name={DASHBOARD_LABEL}
          href={DASHBOARD_HREF}
          active={isDashboardSlug(slug)}
          icon={<IconTags size={DASHBOARD_ICON_SIZE} stroke={DASHBOARD_ICON_STROKE} aria-hidden />}
        />
      </Box>
    );
  }

  return (
    <Box role="listitem" className="relative mr-8 block">
      <Box
        href={DASHBOARD_HREF}
        aria-label={DASHBOARD_ARIA_LABEL}
        aria-current={isDashboardSlug(slug) ? 'page' : undefined}
        analyticsName="cosmos-mega-nav-dashboard"
        className="inline-flex h-full items-center gap-2 px-4 py-2 text-od-content-primary no-underline hover:text-od-content-hover-accent-brand"
      >
        <IconTags size={DASHBOARD_ICON_SIZE} stroke={DASHBOARD_ICON_STROKE} aria-hidden />
        <Text as="span" className="text-label-md text-inherit">
          {DASHBOARD_LABEL}
        </Text>
      </Box>
    </Box>
  );
};

const DashboardNavSkeleton: React.FC<{ variant: DashboardNavVariant }> = ({ variant }) => {
  if (variant === 'glass') {
    return (
      <Box role="listitem" aria-hidden className="relative mr-8 block">
        <Box className="inline-flex h-10 items-center px-3">
          <Box
            className={`skeleton h-4 ${DASHBOARD_SKELETON_WIDTH_CLASS} rounded-md bg-od-bg-primary/20`}
          />
        </Box>
      </Box>
    );
  }

  return (
    <Box role="listitem" aria-hidden className="relative mr-8 block">
      <Box className="inline-flex h-full items-center px-4 py-2">
        <Box
          className={`skeleton h-4 ${DASHBOARD_SKELETON_WIDTH_CLASS} rounded-md bg-od-bg-tertiary`}
        />
      </Box>
    </Box>
  );
};
